
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

export interface Domain {
  id: string
  domain: string
  status?: string
  health_score?: number
  reputation_score?: number
  total_mailboxes?: number
  active_mailboxes?: number
  daily_limit?: number
  current_daily_sent?: number
  bounce_rate?: number
  total_emails_sent?: number
  total_email_replies?: number
  total_email_bounces?: number
  emails_sent_7d?: number
  bounces_7d?: number
  replies_7d?: number
  notes?: string
  domain_registrar?: string
  mailbox_provider?: string
  client_name?: string
  smartlead_status?: boolean
  breakgold_status?: boolean
  clickup_status?: boolean
  mastersheet_status?: boolean
  email_provider?: string
  csv_file_name?: string
  purchase_date?: string
  renewal_date?: string
  expiry_date?: string
  auto_renewal?: boolean
  price?: number | null
  created_at?: string
  updated_at?: string
}

export function useDomains(clientName?: string) {
  return useQuery({
    queryKey: ['domains', clientName ?? 'all'],
    queryFn: async (): Promise<Domain[]> => {
      let query = supabase
        .from('domains')
        .select('*')

      // Filter by client_name when scoped to a specific client
      if (clientName) {
        query = query.eq('client_name', clientName)
      }

      query = query
        .order('purchase_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })

      const { data, error } = await query

      if (error) {
        console.error('Error fetching domains:', error)
        throw error
      }

      return data || []
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useUpdateDomain() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, field, value }: { id: string, field: string, value: string }) => {
      console.log('Updating domain field:', { id, field, value })
      
      const { data, error } = await supabase.rpc('update_domain_field', {
        domain_id_param: id,
        field_name: field,
        field_value: value
      })

      if (error) {
        console.error('Error updating domain:', error)
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
    },
  })
}

export function useDeleteDomain() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string) => {
      console.log('Deleting domain with id:', id)
      
      const { error } = await supabase
        .from('domains')
        .delete()
        .eq('id', id)

      if (error) {
        console.error('Error deleting domain:', error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
    },
  })
}
